import React, { useEffect, useState } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { toast } from 'react-hot-toast';
import {
  FiUsers, FiPlus, FiSearch, FiRefreshCw, FiDownload, FiEdit, FiTrash2,
  FiEye, FiRotateCcw, FiMonitor, FiShield, FiActivity, FiCalendar,
  FiX, FiCopy, FiCheck, FiAlertTriangle, FiCheckCircle, FiXCircle,
  FiClock, FiKey, FiHardDrive, FiSave, FiChevronUp, FiChevronDown
} from 'react-icons/fi';

// Import shared admin components
import {
  AdminContainer, AdminHeader, HeaderContent, HeaderText, HeaderActions,
  ActionButton, MainContent, StatsGrid, StatCard, StatHeader, StatIcon,
  StatValue, StatLabel, SearchAndFilters, SearchBox, FilterSelect,
  ActionButtonGroup, DataTable, TableActionButton, Modal, ModalContent,
  ModalActions, ResultsCounter, LoadingSpinner, ErrorMessage
} from '../../styles/AdminComponents';

// Camera-specific styled components
import styled from 'styled-components';

const ModalFormGroup = styled.div`
  margin-bottom: 1.5rem;
  
  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: #374151;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  input, select, textarea {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    font-size: 1rem;
    
    &:focus {
      outline: none;
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
    
    &:disabled {
      background: #f3f4f6;
      color: #6b7280;
    }
  }
  
  textarea {
    resize: vertical;
    min-height: 100px;
  }
  
  input[type="checkbox"] {
    width: auto;
    margin-right: 0.5rem;
  }
  
  small {
    display: block;
    margin-top: 0.25rem;
    font-size: 0.85rem;
    color: #6b7280;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormFullWidth = styled.div`
  grid-column: 1 / -1;
`;

// Enhanced camera user card for mobile
const CameraUserCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  
  .user-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 1rem;
    gap: 1rem;
    
    .user-info {
      flex: 1;
      min-width: 0;
      
      .user-name {
        font-size: 1.1rem;
        font-weight: 600;
        color: #111827;
        margin-bottom: 0.25rem;
      }
      
      .access-code {
        font-size: 0.8rem;
        font-family: 'Monaco', 'Menlo', monospace;
        background: #dbeafe;
        color: #1d4ed8;
        padding: 0.25rem 0.5rem;
        border-radius: 0.25rem;
        display: inline-block;
      }
    }
    
    .actions {
      display: flex;
      gap: 0.5rem;
      flex-shrink: 0;
      flex-wrap: wrap;
    }
  }
  
  .user-details {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin-bottom: 1rem;
    
    @media (max-width: 480px) {
      grid-template-columns: 1fr;
      gap: 0.75rem;
    }
    
    .detail-item {
      .label {
        font-size: 0.75rem;
        font-weight: 600;
        color: #6b7280;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        margin-bottom: 0.25rem;
      }
      
      .value {
        font-size: 0.9rem;
        color: #111827;
        font-weight: 500;
      }
    }
  }
  
  .device-info {
    background: #f8fafc;
    border-radius: 8px;
    padding: 1rem;
    margin-bottom: 1rem;
    border: 1px solid #e5e7eb;
    
    .device-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 0.5rem;
      
      .device-count {
        font-weight: 600;
        color: #374151;
        font-size: 1rem;
      }
      
      .device-status {
        font-size: 0.8rem;
        padding: 0.2rem 0.5rem;
        border-radius: 12px;
        font-weight: 500;
      }
    }
    
    .hwid-display {
      font-family: 'Monaco', 'Consolas', monospace;
      font-size: 0.8rem;
      background: white;
      padding: 0.5rem;
      border-radius: 0.25rem;
      color: #6b7280;
      border: 1px solid #e5e7eb;
    }
  }
`;

// Responsive container that switches between table and cards
const ResponsiveContainer = styled.div`
  .desktop-view {
    display: block;
    
    @media (max-width: 768px) {
      display: none;
    }
  }
  
  .mobile-view {
    display: none;
    
    @media (max-width: 768px) {
      display: block;
    }
  }
`;

// Enhanced modals for different functionalities
const CameraModal = styled(Modal)`
  .modal-content {
    max-width: ${props => props.wide ? '900px' : '600px'};
    max-height: 90vh;
    overflow-y: auto;
  }
`;

const WarningBox = styled.div`
  background: #fef3cd;
  border: 1px solid #fbbf24;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  
  .warning-icon {
    color: #92400e;
    font-size: 1.25rem;
    flex-shrink: 0;
    margin-top: 0.1rem;
  }
  
  .warning-content {
    flex: 1;
    
    .warning-title {
      font-weight: 600;
      color: #92400e;
      margin-bottom: 0.25rem;
    }
    
    .warning-text {
      color: #92400e;
      font-size: 0.9rem;
      margin: 0;
    }
  }
`;

const DeviceGrid = styled.div`
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
`;

const DeviceItem = styled.div`
  padding: 1rem;
  border-bottom: ${props => props.isLast ? 'none' : '1px solid #e5e7eb'};
  background: ${props => props.selected ? '#f0f9ff' : '#fff'};
  transition: background-color 0.2s;
  
  .device-checkbox {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    cursor: pointer;
    
    input[type="checkbox"] {
      margin-top: 0.25rem;
      flex-shrink: 0;
    }
  }
  
  .device-details {
    flex: 1;
    
    .device-name {
      font-weight: 500;
      margin-bottom: 0.25rem;
      color: #111827;
    }
    
    .device-hwid {
      font-family: 'Monaco', 'Consolas', monospace;
      font-size: 0.8rem;
      background: #f3f4f6;
      padding: 0.1rem 0.3rem;
      border-radius: 3px;
      color: #6b7280;
      margin-bottom: 0.25rem;
    }
    
    .device-meta {
      display: flex;
      gap: 1rem;
      font-size: 0.8rem;
      color: #6b7280;
      margin-bottom: 0.25rem;
      flex-wrap: wrap;
    }
  }
`;

const CameraUsersPage = () => {
  const [cameraUsers, setCameraUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [licenseFilter, setLicenseFilter] = useState('all');
  const [cameraStats, setCameraStats] = useState(null);
  const [copiedUsers, setCopiedUsers] = useState(new Set());

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    user_id: '',
    access_code: '',
    device_name: '',
    license_type: 'standard',
    expires_at: '',
    duration: '1month',
    is_active: true,
    notes: '',
    max_devices: 1
  });
  const [formLoading, setFormLoading] = useState(false);

  // History modal states
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyUser, setHistoryUser] = useState(null);
  const [hwidHistory, setHwidHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Devices modal states
  const [showDevicesModal, setShowDevicesModal] = useState(false);
  const [devicesUser, setDevicesUser] = useState(null);
  const [userDevices, setUserDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);

  // Reset modal states
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [resetDevices, setResetDevices] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [resetReason, setResetReason] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => {
    fetchCameraUsers();
  }, []);

  useEffect(() => {
    const filtered = cameraUsers.filter(user => {
      const matchesSearch = !searchTerm || 
        user.user_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.device_name && user.device_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (user.hwid_short && user.hwid_short.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesStatus = statusFilter === 'all' || 
        (statusFilter === 'active' && user.is_active) ||
        (statusFilter === 'inactive' && !user.is_active);
      
      const matchesLicense = licenseFilter === 'all' || user.license_type === licenseFilter;
      
      return matchesSearch && matchesStatus && matchesLicense;
    });

    setFilteredUsers(filtered);
  }, [cameraUsers, searchTerm, statusFilter, licenseFilter]);

  const fetchCameraUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await axios.get('/api/camera/users', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const usersData = response.data.camera_users || [];
        setCameraUsers(usersData);
        
        // Calculate comprehensive camera stats
        const stats = {
          total_users: usersData.length,
          active_users: usersData.filter(u => u.is_active).length,
          inactive_users: usersData.filter(u => !u.is_active).length,
          lifetime_licenses: usersData.filter(u => u.license_type === 'lifetime').length,
          multi_device_users: usersData.filter(u => (u.max_devices || 1) > 1).length,
          full_device_slots: usersData.filter(u => u.devices_full).length,
          total_registered_devices: usersData.reduce((sum, u) => sum + (u.registered_devices || 0), 0),
          recent_logins: usersData.filter(u => {
            if (!u.last_login) return false;
            const dayAgo = new Date();
            dayAgo.setDate(dayAgo.getDate() - 1);
            return new Date(u.last_login) > dayAgo;
          }).length
        };
        setCameraStats(stats);
      } else {
        setError('Failed to fetch camera users');
        toast.error('Failed to fetch camera users');
      }
    } catch (err) {
      console.error('Error fetching camera users:', err);
      setError('Error fetching camera users');
      toast.error('Error fetching camera users');
    } finally {
      setLoading(false);
    }
  };

  const refreshUsers = () => {
    fetchCameraUsers();
  };

  const exportUsers = () => {
    const csvContent = [
      ['User ID', 'Access Code', 'Device Name', 'License Type', 'Status', 'Max Devices', 'Registered Devices', 'Last Login', 'Expires', 'Notes'],
      ...filteredUsers.map(user => [
        user.user_id || '',
        user.access_code || '',
        user.device_name || '',
        user.license_type || '',
        user.is_active ? 'Active' : 'Inactive',
        user.max_devices || 1,
        user.registered_devices || 0,
        user.last_login || '',
        user.expires_at || '',
        user.notes || ''
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'camera_users_export.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  // User management functions
  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      const token = localStorage.getItem('adminToken');
      const newStatus = !currentStatus;
      
      await axios.patch(`/api/camera/users/${userId}/toggle-status`, 
        { is_active: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setCameraUsers(prev => prev.map(user => 
        user.user_id === userId ? { ...user, is_active: newStatus } : user
      ));

      toast.success(`User ${newStatus ? 'activated' : 'deactivated'} successfully!`);
    } catch (err) {
      console.error('Error toggling user status:', err);
      toast.error('Error updating user status');
    }
  };

  // Camera credential generation functions (matches backend exactly)
  const generateCameraCredentials = () => {
    // Generate 10-character user ID (mixed case and numbers)
    const generateRandomId = (length) => {
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
      let result = '';
      for (let i = 0; i < length; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return result;
    };

    // Generate camera access code (DCAM- + 16 hex characters)
    const generateCameraAccessCode = () => {
      const prefix = 'DCAM';
      const randomBytes = new Uint8Array(8);
      crypto.getRandomValues(randomBytes);
      const randomPart = Array.from(randomBytes, byte => byte.toString(16).padStart(2, '0')).join('').toUpperCase();
      return `${prefix}-${randomPart}`;
    };

    return {
      user_id: generateRandomId(10),
      access_code: generateCameraAccessCode()
    };
  };

  const openAddModal = () => {
    setModalMode('add');
    setEditingUser(null);
    setFormData({
      user_id: '',
      access_code: '',
      device_name: '',
      license_type: 'standard',
      expires_at: '',
      duration: '1month',
      is_active: true,
      notes: '',
      max_devices: 1
    });
    setShowModal(true);
  };

  const openEditModal = (user) => {
    setModalMode('edit');
    setEditingUser(user);
    setFormData({
      user_id: user.user_id,
      access_code: user.access_code,
      device_name: user.device_name || '',
      license_type: user.license_type || 'standard',
      expires_at: user.expires_at ? new Date(user.expires_at).toISOString().slice(0, 16) : '',
      duration: '1month',
      is_active: user.is_active,
      notes: user.notes || '',
      max_devices: user.max_devices || 1
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({
      user_id: '',
      access_code: '',
      device_name: '',
      license_type: 'standard',
      expires_at: '',
      duration: '1month',
      is_active: true,
      notes: '',
      max_devices: 1
    });
  };

  const getDeviceLimitForLicense = (licenseType) => {
    const limits = {
      'standard': 1,
      'premium': 3
    };
    return limits[licenseType] || null;
  };

  const handleLicenseTypeChange = (e) => {
    const licenseType = e.target.value;
    const deviceLimit = getDeviceLimitForLicense(licenseType);
    setFormData(prev => ({
      ...prev,
      license_type: licenseType,
      max_devices: deviceLimit !== null ? deviceLimit : prev.max_devices
    }));
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    if (modalMode === 'edit' && (!formData.user_id.trim() || !formData.access_code.trim())) {
      toast.error('User ID and Access Code are required for editing');
      return;
    }

    try {
      setFormLoading(true);
      const token = localStorage.getItem('adminToken');
      
      const submitData = {
        ...formData,
        expires_at: formData.expires_at ? new Date(formData.expires_at).toISOString() : null
      };

      if (modalMode === 'add') {
        const response = await axios.post('/api/camera/users', submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.success) {
          toast.success('Camera user created successfully!');
          fetchCameraUsers();
          closeModal();
        } else {
          toast.error(response.data.message || 'Error creating user');
        }
      } else {
        const response = await axios.patch(`/api/camera/users/${editingUser.user_id}`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.success) {
          toast.success('Camera user updated successfully!');
          fetchCameraUsers();
          closeModal();
        } else {
          toast.error(response.data.message || 'Error updating user');
        }
      }
    } catch (err) {
      console.error('Error submitting form:', err);
      const errorMsg = err.response?.data?.message || `Error ${modalMode === 'add' ? 'creating' : 'updating'} user`;
      toast.error(errorMsg);
    } finally {
      setFormLoading(false);
    }
  };

  // Device management functions
  const handleViewDevices = async (user) => {
    setDevicesUser(user);
    setShowDevicesModal(true);
    setDevicesLoading(true);
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`/api/camera/users/${user.user_id}/devices`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setUserDevices(response.data.devices || []);
      } else {
        toast.error('Failed to fetch user devices');
        setUserDevices([]);
      }
    } catch (err) {
      console.error('Error fetching user devices:', err);
      toast.error('Error fetching user devices');
      setUserDevices([]);
    } finally {
      setDevicesLoading(false);
    }
  };

  const closeDevicesModal = () => {
    setShowDevicesModal(false);
    setDevicesUser(null);
    setUserDevices([]);
  };

  const handleRemoveDevice = async (userId, hwid) => {
    if (!window.confirm('Are you sure you want to remove this device? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      
      await axios.delete(`/api/camera/users/${userId}/devices/${hwid}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      handleViewDevices(devicesUser);
      fetchCameraUsers();
      
      toast.success('Device removed successfully!');
    } catch (err) {
      console.error('Error removing device:', err);
      toast.error('Error removing device');
    }
  };

  // HWID reset functions
  const handleResetHWID = async (user) => {
    setResetUser(user);
    setResetReason('');
    setSelectedDevices([]);
    setShowResetModal(true);
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`/api/camera/users/${user.user_id}/devices`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setResetDevices(response.data.devices || []);
      } else {
        toast.error('Failed to fetch user devices');
        setResetDevices([]);
      }
    } catch (err) {
      console.error('Error fetching devices for reset:', err);
      toast.error('Error fetching devices');
      setResetDevices([]);
    }
  };

  const closeResetModal = () => {
    setShowResetModal(false);
    setResetUser(null);
    setResetDevices([]);
    setSelectedDevices([]);
    setResetReason('');
  };

  const handleDeviceSelection = (hwid, checked) => {
    if (checked) {
      setSelectedDevices(prev => [...prev, hwid]);
    } else {
      setSelectedDevices(prev => prev.filter(id => id !== hwid));
    }
  };

  const handleSelectAllDevices = (checked) => {
    if (checked) {
      setSelectedDevices(resetDevices.map(device => device.hwid));
    } else {
      setSelectedDevices([]);
    }
  };

  const submitDeviceReset = async (resetAll = false) => {
    if (!resetAll && selectedDevices.length === 0) {
      toast.error('Please select at least one device to reset');
      return;
    }

    if (!resetReason.trim()) {
      toast.error('Please provide a reason for the reset');
      return;
    }

    try {
      setResetLoading(true);
      const token = localStorage.getItem('adminToken');
      
      const payload = {
        reason: resetReason,
        reset_all: resetAll,
        devices_to_remove: resetAll ? [] : selectedDevices
      };

      const response = await axios.patch(`/api/camera/users/${resetUser.user_id}/reset-hwid`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success(response.data.message);
        fetchCameraUsers();
        closeResetModal();
      } else {
        toast.error(response.data.message || 'Error resetting devices');
      }
    } catch (err) {
      console.error('Error resetting devices:', err);
      const errorMsg = err.response?.data?.message || 'Error resetting devices';
      toast.error(errorMsg);
    } finally {
      setResetLoading(false);
    }
  };

  // History functions
  const handleViewHistory = async (user) => {
    setHistoryUser(user);
    setShowHistoryModal(true);
    setHistoryLoading(true);
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`/api/camera/users/${user.user_id}/hwid-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setHwidHistory(response.data.history);
      } else {
        toast.error('Failed to fetch HWID history');
        setHwidHistory([]);
      }
    } catch (err) {
      console.error('Error fetching HWID history:', err);
      toast.error('Error fetching HWID history');
      setHwidHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const closeHistoryModal = () => {
    setShowHistoryModal(false);
    setHistoryUser(null);
    setHwidHistory([]);
  };

  // Copy function
  const handleCopyCameraUser = async (user) => {
    const userInfo = `User: ${user.user_id}
Access: ${user.access_code || 'N/A'}
Device limit: ${user.max_devices || 1}
Created: ${formatDate(user.created_at)}
Expires: ${formatDate(user.expires_at)}`;

    try {
      await navigator.clipboard.writeText(userInfo);
      setCopiedUsers(prev => new Set([...prev, user.user_id]));
      toast.success('User info copied to clipboard!');
      
      // Remove from copied set after 2 seconds
      setTimeout(() => {
        setCopiedUsers(prev => {
          const newSet = new Set(prev);
          newSet.delete(user.user_id);
          return newSet;
        });
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      toast.error('Failed to copy to clipboard');
    }
  };

  // Utility functions
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isExpired = (expiresAt) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const getStatusBadge = (status, isExpired = false) => {
    if (isExpired) {
      return (
        <span className="status-badge status-expired">
          <FiXCircle />
          Expired
        </span>
      );
    }
    
    return (
      <span className={`status-badge ${status ? 'status-confirmed' : 'status-pending'}`}>
        {status ? <FiCheckCircle /> : <FiXCircle />}
        {status ? 'Active' : 'Inactive'}
      </span>
    );
  };

  const getLicenseTypeBadge = (type) => {
    const badges = {
      lifetime: { class: 'status-confirmed', color: '#8b5cf6' },
      quarterly: { class: 'status-pending', color: '#f59e0b' },
      monthly: { class: 'status-pending', color: '#3b82f6' },
      standard: { class: 'status-confirmed', color: '#10b981' },
      premium: { class: 'status-confirmed', color: '#8b5cf6' }
    };
    
    const badge = badges[type] || badges.standard;
    
    return (
      <span 
        className="status-badge" 
        style={{ background: badge.color, color: '#fff', textTransform: 'capitalize' }}
      >
        {type}
      </span>
    );
  };

  return (
    <AdminLayout>
      <AdminContainer>
        <AdminHeader>
          <HeaderContent>
            <HeaderText>
              <h1>Camera Users</h1>
              <p>Manage camera software users, devices, and licenses</p>
            </HeaderText>
            <HeaderActions>
              <ActionButton onClick={openAddModal}>
                <FiPlus />
                <span className="btn-text">Add User</span>
              </ActionButton>
              <ActionButton onClick={refreshUsers} variant="secondary">
                <FiRefreshCw />
                <span className="btn-text">Refresh</span>
              </ActionButton>
              <ActionButton onClick={exportUsers} variant="secondary">
                <FiDownload />
                <span className="btn-text">Export CSV</span>
              </ActionButton>
            </HeaderActions>
          </HeaderContent>
        </AdminHeader>

        <MainContent>
          {/* Enhanced Camera Stats Section */}
          {cameraStats && (
            <StatsGrid>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiUsers color="#3b82f6" /></StatIcon>
                  <h3>Total Users</h3>
                </StatHeader>
                <StatValue>{cameraStats.total_users}</StatValue>
                <StatLabel>Camera Users</StatLabel>
              </StatCard>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiCheckCircle color="#10b981" /></StatIcon>
                  <h3>Active Users</h3>
                </StatHeader>
                <StatValue>{cameraStats.active_users}</StatValue>
                <StatLabel>Currently Active</StatLabel>
              </StatCard>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiXCircle color="#ef4444" /></StatIcon>
                  <h3>Inactive Users</h3>
                </StatHeader>
                <StatValue>{cameraStats.inactive_users}</StatValue>
                <StatLabel>Deactivated</StatLabel>
              </StatCard>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiShield color="#8b5cf6" /></StatIcon>
                  <h3>Lifetime Licenses</h3>
                </StatHeader>
                <StatValue>{cameraStats.lifetime_licenses}</StatValue>
                <StatLabel>Permanent Access</StatLabel>
              </StatCard>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiMonitor color="#f59e0b" /></StatIcon>
                  <h3>Multi-Device</h3>
                </StatHeader>
                <StatValue>{cameraStats.multi_device_users}</StatValue>
                <StatLabel>Multiple Devices</StatLabel>
              </StatCard>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiAlertTriangle color="#f59e0b" /></StatIcon>
                  <h3>Full Slots</h3>
                </StatHeader>
                <StatValue>{cameraStats.full_device_slots}</StatValue>
                <StatLabel>Maxed Out Users</StatLabel>
              </StatCard>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiHardDrive color="#06b6d4" /></StatIcon>
                  <h3>Total Devices</h3>
                </StatHeader>
                <StatValue>{cameraStats.total_registered_devices}</StatValue>
                <StatLabel>Registered Devices</StatLabel>
              </StatCard>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiActivity color="#10b981" /></StatIcon>
                  <h3>Recent Logins</h3>
                </StatHeader>
                <StatValue>{cameraStats.recent_logins}</StatValue>
                <StatLabel>Last 24 Hours</StatLabel>
              </StatCard>
            </StatsGrid>
          )}

          <SearchAndFilters>
            <SearchBox>
              <FiSearch />
              <input
                type="text"
                placeholder="Search by User ID, Device Name, or HWID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchBox>
            <FilterSelect value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </FilterSelect>
            <FilterSelect value={licenseFilter} onChange={(e) => setLicenseFilter(e.target.value)}>
              <option value="all">All Licenses</option>
              <option value="lifetime">Lifetime</option>
              <option value="quarterly">Quarterly</option>
              <option value="monthly">Monthly</option>
              <option value="standard">Standard</option>
              <option value="premium">Premium</option>
            </FilterSelect>
          </SearchAndFilters>

          <ActionButtonGroup>
            <ResultsCounter>{filteredUsers.length} camera users found</ResultsCounter>
          </ActionButtonGroup>

          {loading ? (
            <LoadingSpinner />
          ) : error ? (
            <ErrorMessage>{error}</ErrorMessage>
          ) : (
            <ResponsiveContainer>
              {/* Desktop Table View */}
              <div className="desktop-view">
                <DataTable>
                  <thead>
                    <tr>
                      <th>User ID</th>
                      <th>Access Code</th>
                      <th>Device</th>
                      <th>Devices</th>
                      <th>License</th>
                      <th>Status</th>
                      <th>Logins</th>
                      <th>Resets</th>
                      <th>Last Login</th>
                      <th>Expires</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.length === 0 ? (
                      <tr>
                        <td colSpan="11" style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                          {searchTerm || statusFilter !== 'all' || licenseFilter !== 'all' 
                            ? 'No camera users match your filters' 
                            : 'No camera users found'}
                        </td>
                      </tr>
                    ) : (
                      filteredUsers.map((user, index) => (
                        <tr key={user.id || index}>
                          <td>
                            <div style={{ fontWeight: '600', color: '#111827' }}>
                              {user.user_id}
                            </div>
                          </td>
                          <td>
                            <code className="code-cell">
                              {user.access_code || 'N/A'}
                            </code>
                          </td>
                          <td>{user.device_name || 'Unknown'}</td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <span style={{ fontWeight: '600', color: '#374151' }}>
                                {user.registered_devices || 0}/{user.max_devices || 1}
                              </span>
                              <span 
                                className={`status-badge ${user.devices_full ? 'status-pending' : 'status-confirmed'}`}
                                style={{ fontSize: '0.7rem', padding: '0.15rem 0.5rem' }}
                              >
                                {user.devices_full ? 'Full' : 'Available'}
                              </span>
                            </div>
                            {user.hwid_short && (
                              <div style={{ marginTop: '0.25rem', fontSize: '0.8rem', color: '#6b7280' }}>
                                Primary: <code className="code-cell">{user.hwid_short}</code>
                              </div>
                            )}
                          </td>
                          <td>{getLicenseTypeBadge(user.license_type)}</td>
                          <td>
                            {getStatusBadge(user.is_active, isExpired(user.expires_at))}
                          </td>
                          <td>{user.login_count || 0}</td>
                          <td>
                            <span style={{ 
                              color: (user.hwid_reset_count || 0) > 3 ? '#ef4444' : (user.hwid_reset_count || 0) > 1 ? '#f59e0b' : '#374151',
                              fontWeight: (user.hwid_reset_count || 0) > 1 ? '600' : 'normal'
                            }}>
                              {user.hwid_reset_count || 0}
                            </span>
                            {(user.hwid_reset_count || 0) > 0 && (
                              <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '0.25rem' }}>
                                Last: {user.last_hwid_reset ? formatDate(user.last_hwid_reset) : 'Unknown'}
                              </div>
                            )}
                          </td>
                          <td>{formatDate(user.last_login)}</td>
                          <td>
                            <span style={{ color: isExpired(user.expires_at) ? '#ef4444' : '#374151' }}>
                              {formatDate(user.expires_at)}
                            </span>
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                              <TableActionButton
                                onClick={() => handleCopyCameraUser(user)}
                                title="Copy user info"
                                style={{ background: copiedUsers.has(user.user_id) ? '#10b981' : '#6366f1' }}
                              >
                                {copiedUsers.has(user.user_id) ? <FiCheck /> : <FiCopy />}
                              </TableActionButton>
                              <TableActionButton
                                onClick={() => openEditModal(user)}
                                title="Edit user"
                              >
                                <FiEdit />
                              </TableActionButton>
                              <TableActionButton
                                onClick={() => handleToggleUserStatus(user.user_id, user.is_active)}
                                title={user.is_active ? 'Deactivate' : 'Activate'}
                                variant={user.is_active ? 'danger' : 'success'}
                                style={{ 
                                  background: user.is_active ? '#ef4444' : '#10b981' 
                                }}
                              >
                                {user.is_active ? <FiXCircle /> : <FiCheckCircle />}
                              </TableActionButton>
                              {(user.hwid_short || (user.registered_devices || 0) > 0) && (
                                <TableActionButton
                                  onClick={() => handleResetHWID(user)}
                                  title="Reset devices"
                                  style={{ background: '#f59e0b' }}
                                >
                                  <FiRotateCcw />
                                </TableActionButton>
                              )}
                              <TableActionButton
                                onClick={() => handleViewDevices(user)}
                                title="View devices"
                                style={{ background: '#8b5cf6' }}
                              >
                                <FiMonitor />
                              </TableActionButton>
                              {(user.hwid_reset_count || 0) > 0 && (
                                <TableActionButton
                                  onClick={() => handleViewHistory(user)}
                                  title="View history"
                                  style={{ background: '#6366f1' }}
                                >
                                  <FiEye />
                                </TableActionButton>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </DataTable>
              </div>

              {/* Enhanced Mobile Card View */}
              <div className="mobile-view">
                {filteredUsers.map((user, index) => (
                  <CameraUserCard key={user.id || index}>
                    <div className="user-header">
                      <div className="user-info">
                        <div className="user-name">{user.user_id}</div>
                        <div className="access-code">{user.access_code || 'N/A'}</div>
                      </div>
                      <div className="actions">
                        <TableActionButton
                          onClick={() => handleCopyCameraUser(user)}
                          title="Copy user info"
                          style={{ background: copiedUsers.has(user.user_id) ? '#10b981' : '#6366f1' }}
                        >
                          {copiedUsers.has(user.user_id) ? <FiCheck /> : <FiCopy />}
                        </TableActionButton>
                        <TableActionButton
                          onClick={() => openEditModal(user)}
                          title="Edit"
                        >
                          <FiEdit />
                        </TableActionButton>
                        <TableActionButton
                          onClick={() => handleToggleUserStatus(user.user_id, user.is_active)}
                          title={user.is_active ? 'Deactivate' : 'Activate'}
                          style={{ background: user.is_active ? '#ef4444' : '#10b981' }}
                        >
                          {user.is_active ? <FiXCircle /> : <FiCheckCircle />}
                        </TableActionButton>
                      </div>
                    </div>
                    
                    <div className="user-details">
                      <div className="detail-item">
                        <div className="label">Device Name</div>
                        <div className="value">{user.device_name || 'Unknown'}</div>
                      </div>
                      <div className="detail-item">
                        <div className="label">License Type</div>
                        <div className="value">{getLicenseTypeBadge(user.license_type)}</div>
                      </div>
                      <div className="detail-item">
                        <div className="label">Status</div>
                        <div className="value">{getStatusBadge(user.is_active, isExpired(user.expires_at))}</div>
                      </div>
                      <div className="detail-item">
                        <div className="label">Last Login</div>
                        <div className="value">{formatDate(user.last_login)}</div>
                      </div>
                    </div>

                    <div className="device-info">
                      <div className="device-header">
                        <div className="device-count">
                          Devices: {user.registered_devices || 0}/{user.max_devices || 1}
                        </div>
                        <div 
                          className="device-status"
                          style={{
                            background: user.devices_full ? '#fef3c7' : '#dcfce7',
                            color: user.devices_full ? '#92400e' : '#166534'
                          }}
                        >
                          {user.devices_full ? 'Full' : 'Available'}
                        </div>
                      </div>
                      {user.hwid_short && (
                        <div className="hwid-display">
                          Primary HWID: {user.hwid_short}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                        {(user.hwid_short || (user.registered_devices || 0) > 0) && (
                          <TableActionButton
                            onClick={() => handleResetHWID(user)}
                            title="Reset devices"
                            style={{ background: '#f59e0b' }}
                          >
                            <FiRotateCcw />
                          </TableActionButton>
                        )}
                        <TableActionButton
                          onClick={() => handleViewDevices(user)}
                          title="View devices"
                          style={{ background: '#8b5cf6' }}
                        >
                          <FiMonitor />
                        </TableActionButton>
                        {(user.hwid_reset_count || 0) > 0 && (
                          <TableActionButton
                            onClick={() => handleViewHistory(user)}
                            title="View history"
                            style={{ background: '#6366f1' }}
                          >
                            <FiEye />
                          </TableActionButton>
                        )}
                      </div>
                    </div>
                  </CameraUserCard>
                ))}
              </div>
            </ResponsiveContainer>
          )}

          {/* Create/Edit User Modal */}
          {showModal && (
            <CameraModal>
              <ModalContent>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                  <h2>
                    {modalMode === 'add' ? '🎥 Create Camera User' : '🎥 Edit Camera User'}
                  </h2>
                  <button 
                    onClick={closeModal}
                    style={{ 
                      background: 'none', 
                      border: 'none', 
                      fontSize: '1.5rem', 
                      cursor: 'pointer',
                      color: '#6b7280',
                      padding: '0.5rem',
                      borderRadius: '0.25rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <FiX />
                  </button>
                </div>

                <form onSubmit={handleFormSubmit}>
                  {modalMode === 'add' && (
                    <div style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
                      <button
                        type="button"
                        onClick={() => {
                          const credentials = generateCameraCredentials();
                          setFormData(prev => ({ 
                            ...prev, 
                            user_id: credentials.user_id,
                            access_code: credentials.access_code
                          }));
                        }}
                        style={{
                          padding: '0.75rem 1.5rem',
                          background: '#10b981',
                          color: 'white',
                          border: 'none',
                          borderRadius: '0.5rem',
                          cursor: 'pointer',
                          fontWeight: '500',
                          fontSize: '0.9rem'
                        }}
                      >
                        Generate Both Credentials
                      </button>
                    </div>
                  )}

                  <FormGrid>
                    <ModalFormGroup>
                      <label>
                        <FiKey />
                        User ID {modalMode === 'add' ? '' : '*'}
                      </label>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <input
                          type="text"
                          name="user_id"
                          value={formData.user_id}
                          onChange={handleFormChange}
                          disabled={modalMode === 'edit'}
                          placeholder={modalMode === 'add' ? 'Leave blank to auto-generate' : ''}
                          required={modalMode === 'edit'}
                          style={{ flex: 1 }}
                        />
                        {modalMode === 'add' && (
                          <button
                            type="button"
                            onClick={() => {
                              const credentials = generateCameraCredentials();
                              setFormData(prev => ({ ...prev, user_id: credentials.user_id }));
                            }}
                            style={{
                              padding: '0.75rem 1rem',
                              background: '#3b82f6',
                              color: 'white',
                              border: 'none',
                              borderRadius: '0.5rem',
                              cursor: 'pointer',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            Generate
                          </button>
                        )}
                      </div>
                      {modalMode === 'add' && (
                        <small>10-character mixed case ID (auto-generated if blank)</small>
                      )}
                    </ModalFormGroup>

                    <ModalFormGroup>
                      <label>
                        <FiShield />
                        Access Code {modalMode === 'add' ? '' : '*'}
                      </label>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <input
                          type="text"
                          name="access_code"
                          value={formData.access_code}
                          onChange={handleFormChange}
                          placeholder={modalMode === 'add' ? 'Leave blank to auto-generate (DCAM-XXXXXXXXXXXXXXXX)' : ''}
                          required={modalMode === 'edit'}
                          style={{ flex: 1 }}
                        />
                        {modalMode === 'add' && (
                          <button
                            type="button"
                            onClick={() => {
                              const credentials = generateCameraCredentials();
                              setFormData(prev => ({ ...prev, access_code: credentials.access_code }));
                            }}
                            style={{
                              padding: '0.75rem 1rem',
                              background: '#3b82f6',
                              color: 'white',
                              border: 'none',
                              borderRadius: '0.5rem',
                              cursor: 'pointer',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            Generate
                          </button>
                        )}
                      </div>
                      {modalMode === 'add' && (
                        <small>DCAM- prefix with 16 hex characters (auto-generated if blank)</small>
                      )}
                    </ModalFormGroup>

                    <ModalFormGroup>
                      <label>
                        <FiMonitor />
                        Device Name
                      </label>
                      <input
                        type="text"
                        name="device_name"
                        value={formData.device_name}
                        onChange={handleFormChange}
                        placeholder="User's device name"
                      />
                    </ModalFormGroup>

                    <ModalFormGroup>
                      <label>
                        <FiShield />
                        License Type
                      </label>
                      <select
                        name="license_type"
                        value={formData.license_type}
                        onChange={handleLicenseTypeChange}
                      >
                        <option value="standard">Standard (1 device)</option>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="premium">Premium (3 devices)</option>
                        <option value="lifetime">Lifetime</option>
                      </select>
                    </ModalFormGroup>

                    <ModalFormGroup>
                      <label>
                        <FiHardDrive />
                        Max Devices (1-10)
                      </label>
                      <input
                        type="number"
                        name="max_devices"
                        value={formData.max_devices}
                        onChange={handleFormChange}
                        min="1"
                        max="10"
                        required
                      />
                      <small>Auto-set for Standard (1) and Premium (3), others manually set</small>
                    </ModalFormGroup>

                    <ModalFormGroup>
                      <label>
                        <FiClock />
                        Duration
                      </label>
                      <select
                        name="duration"
                        value={formData.duration}
                        onChange={handleFormChange}
                      >
                        <option value="1week">1 Week</option>
                        <option value="2weeks">2 Weeks</option>
                        <option value="1month">1 Month</option>
                        <option value="3months">3 Months</option>
                        <option value="6months">6 Months</option>
                        <option value="1year">1 Year</option>
                        <option value="lifetime">Lifetime</option>
                      </select>
                      <small>Or set custom expiration below</small>
                    </ModalFormGroup>

                    <ModalFormGroup>
                      <label>
                        <FiCalendar />
                        Custom Expires At (Optional)
                      </label>
                      <input
                        type="datetime-local"
                        name="expires_at"
                        value={formData.expires_at}
                        onChange={handleFormChange}
                      />
                      <small>Overrides duration setting if specified</small>
                    </ModalFormGroup>

                    <ModalFormGroup>
                      <label>
                        <input
                          type="checkbox"
                          name="is_active"
                          checked={formData.is_active}
                          onChange={handleFormChange}
                        />
                        Active
                      </label>
                    </ModalFormGroup>

                    <FormFullWidth>
                      <ModalFormGroup>
                        <label>Notes</label>
                        <textarea
                          name="notes"
                          value={formData.notes}
                          onChange={handleFormChange}
                          placeholder="Optional notes about this user..."
                        />
                      </ModalFormGroup>
                    </FormFullWidth>
                  </FormGrid>

                  <ModalActions>
                    <ActionButton type="button" onClick={closeModal} variant="secondary">
                      Cancel
                    </ActionButton>
                    <ActionButton type="submit" disabled={formLoading}>
                      <FiSave />
                      {formLoading ? 'Saving...' : (modalMode === 'add' ? 'Create User' : 'Update User')}
                    </ActionButton>
                  </ModalActions>
                </form>
              </ModalContent>
            </CameraModal>
          )}

          {/* Enhanced Devices Modal */}
          {showDevicesModal && devicesUser && (
            <CameraModal wide>
              <ModalContent>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                  <h2>🖥️ Registered Devices - {devicesUser.user_id}</h2>
                  <button 
                    onClick={closeDevicesModal}
                    style={{ 
                      background: 'none', 
                      border: 'none', 
                      fontSize: '1.5rem', 
                      cursor: 'pointer',
                      color: '#6b7280',
                      padding: '0.5rem',
                      borderRadius: '0.25rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <FiX />
                  </button>
                </div>

                {/* Device Summary */}
                <div style={{ 
                  background: '#f8fafc', 
                  borderRadius: '8px', 
                  padding: '1.5rem', 
                  marginBottom: '2rem',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                    gap: '1rem' 
                  }}>
                    <div>
                      <div style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '0.25rem' }}>Device Slots</div>
                      <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#374151' }}>
                        {userDevices.length}/{devicesUser.max_devices || 1}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '0.25rem' }}>Status</div>
                      <span 
                        className="status-badge"
                        style={{
                          background: userDevices.length >= (devicesUser.max_devices || 1) ? '#f59e0b' : '#10b981',
                          color: '#fff',
                          fontSize: '0.8rem',
                          padding: '0.25rem 0.75rem'
                        }}
                      >
                        {userDevices.length >= (devicesUser.max_devices || 1) ? 'Full' : 'Available'}
                      </span>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '0.25rem' }}>Available Slots</div>
                      <div style={{ 
                        fontSize: '1.5rem', 
                        fontWeight: '700', 
                        color: userDevices.length >= (devicesUser.max_devices || 1) ? '#ef4444' : '#10b981' 
                      }}>
                        {Math.max(0, (devicesUser.max_devices || 1) - userDevices.length)}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '0.25rem' }}>License Type</div>
                      <div style={{ 
                        fontSize: '1rem', 
                        fontWeight: '600', 
                        color: '#374151', 
                        textTransform: 'capitalize' 
                      }}>
                        {devicesUser.license_type || 'Standard'}
                      </div>
                    </div>
                  </div>
                </div>

                {devicesLoading ? (
                  <LoadingSpinner />
                ) : userDevices.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
                    <FiMonitor size={48} style={{ marginBottom: '1rem' }} />
                    <p>No devices registered</p>
                  </div>
                ) : (
                  <div style={{ maxHeight: '500px', overflowY: 'auto', overflowX: 'auto' }}>
                    <DataTable style={{ minWidth: '900px' }}>
                      <thead>
                        <tr>
                          <th>Device Name</th>
                          <th>HWID</th>
                          <th>Source</th>
                          <th>Registered</th>
                          <th>Last Login</th>
                          <th>Login Count</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userDevices.map((device, index) => (
                          <tr key={device.hwid || index}>
                            <td>
                              <div style={{ fontWeight: '500' }}>{device.device_name || 'Unknown Device'}</div>
                              {device.source === 'legacy' && (
                                <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '0.25rem' }}>
                                  Legacy Device
                                </div>
                              )}
                            </td>
                            <td>
                              <code className="code-cell">
                                {device.hwid_short || device.hwid?.substring(0, 12) + '...' || 'N/A'}
                              </code>
                            </td>
                            <td>
                              <span 
                                className="status-badge"
                                style={{
                                  background: device.source === 'legacy' ? '#6b7280' : '#3b82f6',
                                  color: '#fff',
                                  fontSize: '0.7rem',
                                  padding: '0.15rem 0.5rem'
                                }}
                              >
                                {device.source === 'legacy' ? 'Legacy' : 'Multi-Device'}
                              </span>
                            </td>
                            <td>{formatDate(device.registered_at)}</td>
                            <td>{formatDate(device.last_login)}</td>
                            <td>{device.login_count || 0}</td>
                            <td>
                              <span className="status-badge status-confirmed">
                                <FiCheckCircle />
                                Active
                              </span>
                            </td>
                            <td>
                              <TableActionButton
                                onClick={() => handleRemoveDevice(devicesUser.user_id, device.hwid)}
                                variant="danger"
                                title="Remove device"
                              >
                                <FiTrash2 />
                              </TableActionButton>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </DataTable>
                  </div>
                )}

                <ModalActions>
                  <ActionButton onClick={closeDevicesModal}>
                    Close
                  </ActionButton>
                </ModalActions>
              </ModalContent>
            </CameraModal>
          )}

          {/* Enhanced Reset Devices Modal */}
          {showResetModal && resetUser && (
            <CameraModal wide>
              <ModalContent>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                  <h2>🔄 Reset Devices - {resetUser.user_id}</h2>
                  <button 
                    onClick={closeResetModal}
                    style={{ 
                      background: 'none', 
                      border: 'none', 
                      fontSize: '1.5rem', 
                      cursor: 'pointer',
                      color: '#6b7280',
                      padding: '0.5rem',
                      borderRadius: '0.25rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <FiX />
                  </button>
                </div>

                <WarningBox>
                  <FiAlertTriangle className="warning-icon" />
                  <div className="warning-content">
                    <div className="warning-title">Warning: Device Reset</div>
                    <p className="warning-text">
                      Selected devices will be permanently removed and users will need to re-authenticate from those devices. This action cannot be undone.
                    </p>
                  </div>
                </WarningBox>

                {resetDevices.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
                    <FiMonitor size={48} style={{ marginBottom: '1rem' }} />
                    <p>No devices found for this user</p>
                  </div>
                ) : (
                  <>
                    <ModalFormGroup>
                      <label>Select devices to reset:</label>
                      <div style={{ marginTop: '0.5rem', marginBottom: '1rem' }}>
                        <label style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '0.5rem', 
                          cursor: 'pointer',
                          fontWeight: '500'
                        }}>
                          <input
                            type="checkbox"
                            checked={selectedDevices.length === resetDevices.length && resetDevices.length > 0}
                            onChange={(e) => handleSelectAllDevices(e.target.checked)}
                          />
                          Select All Devices
                        </label>
                      </div>

                      <DeviceGrid>
                        {resetDevices.map((device, index) => (
                          <DeviceItem 
                            key={device.hwid || index}
                            isLast={index === resetDevices.length - 1}
                            selected={selectedDevices.includes(device.hwid)}
                          >
                            <label className="device-checkbox">
                              <input
                                type="checkbox"
                                checked={selectedDevices.includes(device.hwid)}
                                onChange={(e) => handleDeviceSelection(device.hwid, e.target.checked)}
                              />
                              <div className="device-details">
                                <div className="device-name">
                                  {device.device_name || 'Unknown Device'}
                                </div>
                                <div className="device-hwid">
                                  HWID: {device.hwid_short || device.hwid?.substring(0, 12) + '...' || 'N/A'}
                                </div>
                                <div className="device-meta">
                                  <span>Registered: {formatDate(device.registered_at)}</span>
                                  <span>Last Login: {formatDate(device.last_login)}</span>
                                  <span>Logins: {device.login_count || 0}</span>
                                </div>
                                {device.source === 'legacy' && (
                                  <span 
                                    className="status-badge"
                                    style={{ 
                                      background: '#6b7280', 
                                      fontSize: '0.7rem', 
                                      padding: '0.1rem 0.4rem',
                                      marginTop: '0.25rem'
                                    }}
                                  >
                                    Legacy Device
                                  </span>
                                )}
                              </div>
                            </label>
                          </DeviceItem>
                        ))}
                      </DeviceGrid>
                    </ModalFormGroup>

                    <ModalFormGroup>
                      <label>
                        <FiEdit />
                        Reason for Reset *
                      </label>
                      <textarea
                        value={resetReason}
                        onChange={(e) => setResetReason(e.target.value)}
                        placeholder="Please provide a reason for resetting these devices..."
                        required
                      />
                    </ModalFormGroup>
                  </>
                )}

                <ModalActions>
                  <ActionButton onClick={closeResetModal} variant="secondary">
                    Cancel
                  </ActionButton>
                  {resetDevices.length > 0 && (
                    <>
                      <ActionButton
                        onClick={() => submitDeviceReset(false)}
                        disabled={resetLoading || selectedDevices.length === 0 || !resetReason.trim()}
                        style={{ background: '#f59e0b', borderColor: '#f59e0b' }}
                      >
                        <FiRotateCcw />
                        {resetLoading ? 'Resetting...' : `Reset Selected (${selectedDevices.length})`}
                      </ActionButton>
                      <ActionButton
                        onClick={() => submitDeviceReset(true)}
                        disabled={resetLoading || !resetReason.trim()}
                        variant="danger"
                      >
                        <FiRotateCcw />
                        {resetLoading ? 'Resetting...' : 'Reset All Devices'}
                      </ActionButton>
                    </>
                  )}
                </ModalActions>
              </ModalContent>
            </CameraModal>
          )}

          {/* Enhanced History Modal */}
          {showHistoryModal && historyUser && (
            <CameraModal wide>
              <ModalContent>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                  <h2>📋 HWID History - {historyUser.user_id}</h2>
                  <button 
                    onClick={closeHistoryModal}
                    style={{ 
                      background: 'none', 
                      border: 'none', 
                      fontSize: '1.5rem', 
                      cursor: 'pointer',
                      color: '#6b7280',
                      padding: '0.5rem',
                      borderRadius: '0.25rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <FiX />
                  </button>
                </div>

                {historyLoading ? (
                  <LoadingSpinner />
                ) : hwidHistory.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
                    <FiActivity size={48} style={{ marginBottom: '1rem' }} />
                    <p>No HWID history found</p>
                  </div>
                ) : (
                  <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <DataTable>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Action</th>
                          <th>Old HWID</th>
                          <th>New HWID</th>
                          <th>Performed By</th>
                          <th>Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {hwidHistory.map((entry) => (
                          <tr key={entry.id}>
                            <td>{formatDate(entry.created_at)}</td>
                            <td>
                              <span 
                                className="status-badge"
                                style={{
                                  background: entry.action_type === 'reset' ? '#ef4444' : 
                                            entry.action_type === 'initial' ? '#10b981' : '#f59e0b',
                                  color: '#fff',
                                  textTransform: 'capitalize'
                                }}
                              >
                                {entry.action_type}
                              </span>
                            </td>
                            <td>
                              {entry.old_hwid_short ? (
                                <code className="code-cell">{entry.old_hwid_short}</code>
                              ) : (
                                <span style={{ color: '#6b7280', fontStyle: 'italic' }}>None</span>
                              )}
                            </td>
                            <td>
                              {entry.new_hwid_short ? (
                                <code className="code-cell">{entry.new_hwid_short}</code>
                              ) : (
                                <span style={{ color: '#6b7280', fontStyle: 'italic' }}>None</span>
                              )}
                            </td>
                            <td>{entry.performed_by || 'System'}</td>
                            <td style={{ maxWidth: '200px', wordWrap: 'break-word' }}>
                              {entry.reason || 'No reason provided'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </DataTable>
                  </div>
                )}

                <ModalActions>
                  <ActionButton onClick={closeHistoryModal}>
                    Close
                  </ActionButton>
                </ModalActions>
              </ModalContent>
            </CameraModal>
          )}
        </MainContent>
      </AdminContainer>
    </AdminLayout>
  );
};

export default CameraUsersPage; 